import { useEffect, useState } from "react";
import axios from "axios";
import {
    MapPin,
    Briefcase,
    Calendar,
    Building2,
    ArrowUpRight,
    X,
    Send,
    Loader2,
    Check,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useReveal } from "../hooks/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ApplyModal({ job, onClose }) {
    const { t, lang } = useLang();
    const m = t.careers.modal;
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        cv_link: "",
        message: "",
    });
    const [state, setState] = useState({ loading: false, success: false, error: "" });

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            setState((s) => ({ ...s, error: m.requiredError }));
            return;
        }
        setState({ loading: true, success: false, error: "" });
        try {
            if (job.id === "spontaneous") {
                // Spontaneous application → route through /api/contact
                const msgParts = [];
                if (form.cv_link) msgParts.push(`CV : ${form.cv_link}`);
                if (form.message) msgParts.push(form.message);
                const composedMessage =
                    msgParts.join("\n\n") ||
                    (lang === "fr"
                        ? "Candidature spontanée — pas de message complémentaire."
                        : "Spontaneous application — no additional message.");
                await axios.post(`${API}/contact`, {
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    service: job.title,
                    message: composedMessage,
                    language: lang,
                });
            } else {
                await axios.post(`${API}/jobs/${job.id}/apply`, {
                    ...form,
                    language: lang,
                });
            }
            setState({ loading: false, success: true, error: "" });
        } catch (err) {
            setState({ loading: false, success: false, error: m.errorMsg });
        }
    };

    return (
        <div
            data-testid="apply-modal"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative bg-white border-2 border-ink max-w-xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center hover:bg-paper-alt transition-colors"
                    data-testid="apply-modal-close"
                >
                    <X size={20} />
                </button>
                <div className="p-6 md:p-10">
                    {state.success ? (
                        <div data-testid="apply-success" className="text-center py-6">
                            <span className="inline-flex w-16 h-16 bg-mint items-center justify-center mb-5">
                                <Check size={30} className="text-ink" />
                            </span>
                            <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-ink">
                                {m.successTitle}
                            </h3>
                            <p className="mt-4 text-ink-soft">{m.successMsg}</p>
                            <button onClick={onClose} className="mt-8 btn-primary">
                                {m.close}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted mb-2">
                                {job.title}
                            </div>
                            <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-ink leading-tight">
                                {m.title}
                            </h3>
                            <p className="mt-3 text-sm text-ink-soft">{m.subtitle}</p>

                            <form
                                onSubmit={submit}
                                data-testid="apply-form"
                                className="mt-6 space-y-4"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">{m.fields.name} *</label>
                                        <input
                                            data-testid="apply-name"
                                            name="name"
                                            value={form.name}
                                            onChange={handle}
                                            className="field"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label">{m.fields.email} *</label>
                                        <input
                                            data-testid="apply-email"
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={handle}
                                            className="field"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label">{m.fields.phone} *</label>
                                        <input
                                            data-testid="apply-phone"
                                            name="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={handle}
                                            className="field"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label">{m.fields.cv}</label>
                                        <input
                                            data-testid="apply-cv"
                                            name="cv_link"
                                            type="url"
                                            placeholder="https://…"
                                            value={form.cv_link}
                                            onChange={handle}
                                            className="field"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="label">{m.fields.message}</label>
                                    <textarea
                                        data-testid="apply-message"
                                        name="message"
                                        rows={4}
                                        value={form.message}
                                        onChange={handle}
                                        className="field resize-none"
                                    />
                                </div>
                                {state.error && (
                                    <div className="px-4 py-3 border border-destructive text-destructive text-sm">
                                        {state.error}
                                    </div>
                                )}
                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-5 py-3 font-display text-xs uppercase tracking-widest text-ink hover:text-ink-muted"
                                    >
                                        {m.cancel}
                                    </button>
                                    <button
                                        type="submit"
                                        data-testid="apply-submit"
                                        disabled={state.loading}
                                        className="btn-primary disabled:opacity-50"
                                    >
                                        {state.loading ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                {m.submitting}
                                            </>
                                        ) : (
                                            <>
                                                <Send size={16} />
                                                {m.submit}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function JobCard({ job, onApply, index }) {
    const { t } = useLang();
    const c = t.careers;
    const { ref, className } = useReveal(0.1);
    return (
        <article
            ref={ref}
            data-testid={`job-card-${job.id}`}
            style={{ transitionDelay: `${(index % 3) * 80}ms` }}
            className={`${className} group bg-white border border-border hover:border-ink transition-all p-6 lg:p-8 flex flex-col`}
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                <span className="inline-flex items-center gap-2 bg-mint text-ink px-3 py-1 font-display text-xs uppercase tracking-widest">
                    <Briefcase size={12} />
                    {job.type}
                </span>
                <span className="font-display text-xs uppercase tracking-widest text-ink-muted">
                    {new Date(job.created_at).toLocaleDateString()}
                </span>
            </div>
            <h3 className="font-display font-bold uppercase text-xl md:text-2xl text-ink leading-tight tracking-tight">
                {job.title}
            </h3>
            <div className="mt-4 space-y-2 text-sm text-ink-soft">
                <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-mint-hover" />
                    <span>{job.location}</span>
                </div>
                {job.department && (
                    <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-mint-hover" />
                        <span>{job.department}</span>
                    </div>
                )}
                {job.deadline && (
                    <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-mint-hover" />
                        <span>
                            {c.deadline}: {job.deadline}
                        </span>
                    </div>
                )}
            </div>
            <p className="mt-5 text-sm text-ink-soft leading-relaxed flex-1 whitespace-pre-line">
                {job.description.length > 240
                    ? job.description.slice(0, 240) + "…"
                    : job.description}
            </p>
            <button
                data-testid={`apply-btn-${job.id}`}
                onClick={() => onApply(job)}
                className="mt-6 inline-flex items-center justify-between gap-2 bg-ink text-white hover:bg-mint hover:text-ink transition-colors px-6 py-3 font-display uppercase tracking-widest text-xs w-full"
            >
                <span>{c.apply}</span>
                <ArrowUpRight size={16} />
            </button>
        </article>
    );
}

export default function Careers() {
    const { t } = useLang();
    const c = t.careers;
    const head = useReveal();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [applyJob, setApplyJob] = useState(null);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/jobs`)
            .then((res) => {
                if (mounted) {
                    setJobs(res.data.items || []);
                    setLoading(false);
                }
            })
            .catch(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <section
            id="careers"
            data-testid="careers-section"
            className="py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div ref={head.ref} className={`${head.className} max-w-3xl mb-12`}>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="w-10 h-px bg-ink" />
                        <span className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted">
                            {c.kicker}
                        </span>
                    </div>
                    <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
                        {c.title}
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-ink-soft max-w-2xl">
                        {c.subtitle}
                    </p>
                </div>

                {loading ? (
                    <div className="py-16 text-center text-ink-muted">
                        <Loader2 className="inline animate-spin" size={20} />
                    </div>
                ) : jobs.length === 0 ? (
                    <div
                        data-testid="no-jobs"
                        className="bg-paper-alt border border-border p-10 text-center"
                    >
                        <p className="text-ink-soft mb-6">{c.noJobs}</p>
                        <button
                            data-testid="spontaneous-apply"
                            onClick={() =>
                                setApplyJob({
                                    id: "spontaneous",
                                    title: c.spontaneous,
                                })
                            }
                            className="btn-secondary"
                        >
                            {c.spontaneous}
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {jobs.map((job, i) => (
                            <JobCard
                                key={job.id}
                                job={job}
                                index={i}
                                onApply={setApplyJob}
                            />
                        ))}
                    </div>
                )}
            </div>

            {applyJob && (
                <ApplyModal job={applyJob} onClose={() => setApplyJob(null)} />
            )}
        </section>
    );
}
